import React, { useRef, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MagicWand, SpeakerHigh, Stop, MusicNotes, Palette, Download, UploadSimple, TextAa, FilmStrip, Clock, Waveform, Microphone, Gift, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { listSoundEffects, playSfx, listVoices, speak, stopSpeaking } from "../lib/studioSounds";
import { recordVideo } from "../lib/videoExporter";
import { uploadMedia } from "../lib/uploadMedia";
import MusicPicker from "../components/MusicPicker";

/** Pre-built color schemes — gradients tuned for legibility with white text on top */
const SCHEMES = [
  { id: "jamaica",    label: "Jamaica",    bg: "linear-gradient(135deg, #009b3a 0%, #fed100 50%, #000 100%)", text: "#fff" },
  { id: "soca_fire",  label: "Soca Fire",  bg: "linear-gradient(135deg, #ff5e3a 0%, #ff9d2e 50%, #ffd02e 100%)", text: "#1a0a00" },
  { id: "carnival",   label: "Carnival",   bg: "linear-gradient(135deg, #ff006e 0%, #8338ec 50%, #3a86ff 100%)", text: "#fff" },
  { id: "sunset",     label: "Sunset",     bg: "linear-gradient(135deg, #fa8072 0%, #ff6f61 50%, #4b1248 100%)", text: "#fff" },
  { id: "ocean",      label: "Ocean",      bg: "linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)", text: "#003b3b" },
  { id: "midnight",   label: "Midnight",   bg: "linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)", text: "#fff" },
  { id: "papaya",     label: "Papaya",     bg: "radial-gradient(circle at 30% 20%, #FF6B2C 0%, #FFA94D 50%, #1a0e08 100%)", text: "#fff" },
  { id: "lignum",     label: "Lignum Vitae","bg": "linear-gradient(135deg, #1a472a 0%, #2d6a4f 50%, #95d5b2 100%)", text: "#fff" },
];

const FONTS = [
  { id: "anton",    label: "Anton",    family: "'Anton', 'Arial Black', sans-serif" },
  { id: "system",   label: "System",   family: "system-ui, sans-serif" },
  { id: "mono",     label: "Mono",     family: "'Courier New', monospace" },
  { id: "serif",    label: "Editorial","family": "'Georgia', serif" },
];

export default function CreatorStudio() {
  const nav = useNavigate();
  const canvasRef = useRef(null);
  const [text, setText] = useState("Soca Sunday 🇯🇲\nLet's go!");
  const [scheme, setScheme] = useState(SCHEMES[0]);
  const [fontIdx, setFontIdx] = useState(0);
  const [fontSize, setFontSize] = useState(72);
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState("");
  const [rate, setRate] = useState(1.0);
  const [speaking, setSpeaking] = useState(false);
  const [music, setMusic] = useState(null);
  const [musicOpen, setMusicOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [sfx] = useState(listSoundEffects());
  const [duration, setDuration] = useState(8);
  const [exporting, setExporting] = useState(null); // { phase, percent } | null

  useEffect(() => {
    const tick = setInterval(() => {
      const v = listVoices();
      if (v.length && voices.length === 0) {
        setVoices(v);
        const def = v.find((x) => x.default) || v[0];
        setVoiceURI(def?.id || "");
      }
    }, 400);
    return () => clearInterval(tick);
  }, [voices.length]);

  // Pure drawing function — reused for both the live preview and animated MP4 frames.
  // progress: 0..1 (1 = static state). Animates: per-line fade-in for 0..0.6, then
  // subtle scale pulse + gradient drift.
  const drawFrame = useCallback((cv, progress = 1) => {
    if (!cv) return;
    const ctx = cv.getContext("2d");
    const W = (cv.width = 720);
    const H = (cv.height = 960);
    const drift = Math.sin(progress * Math.PI * 2) * 60;
    const grad = ctx.createLinearGradient(W * 0.5 + drift, 0, W * 0.5 - drift, H);
    const stops = (scheme.bg.match(/#[0-9a-f]{3,6}/gi) || ["#000", "#000"]);
    stops.forEach((c, i) => grad.addColorStop(i / Math.max(1, stops.length - 1), c));
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);
    const vig = ctx.createRadialGradient(W / 2, H / 2, W * 0.2, W / 2, H / 2, W * 0.7);
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(1, "rgba(0,0,0,0.4)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, W, H);

    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.font = `900 ${fontSize}px ${FONTS[fontIdx].family}`;
    ctx.shadowColor = "rgba(0,0,0,0.45)";
    ctx.shadowBlur = 14;
    ctx.shadowOffsetY = 6;

    const lines = text.split("\n");
    const lineH = fontSize * 1.15;
    const blockH = lines.length * lineH;
    const pulse = progress > 0.6 ? 1 + 0.04 * Math.sin((progress - 0.6) * Math.PI * 6) : 1;
    let y = H / 2 - (blockH * pulse) / 2 + (lineH * pulse) / 2;

    lines.forEach((ln, i) => {
      const lineStart = (i / lines.length) * 0.6;
      const lineEnd = ((i + 1) / lines.length) * 0.6;
      const localProg = Math.max(0, Math.min(1, (progress - lineStart) / Math.max(0.0001, lineEnd - lineStart)));
      const eased = 1 - Math.pow(1 - localProg, 3);
      ctx.globalAlpha = eased;
      const slideY = (1 - eased) * 30;
      ctx.fillStyle = scheme.text;
      ctx.save();
      ctx.translate(W / 2, y + slideY);
      ctx.scale(pulse, pulse);
      ctx.fillText(ln, 0, 0, W * 0.85);
      ctx.restore();
      y += lineH * pulse;
    });
    ctx.globalAlpha = 1;
  }, [text, scheme, fontIdx, fontSize]);

  useEffect(() => {
    drawFrame(canvasRef.current, 1);
  }, [drawFrame]);

  const previewSpeak = () => {
    if (!text.trim()) return;
    setSpeaking(true);
    const u = speak(text, { voiceURI, rate });
    if (u) { u.onend = () => setSpeaking(false); u.onerror = () => setSpeaking(false); }
    else setSpeaking(false);
  };
  const stopSpeak = () => { stopSpeaking(); setSpeaking(false); };

  const aiCaption = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/caption", { hint: text || "energetic dance moment" });
      setText(data.caption);
    } catch { toast.error("AI caption failed"); }
    finally { setAiLoading(false); }
  };

  const postCard = async () => {
    setPosting(true);
    try {
      const cv = canvasRef.current;
      const blob = await new Promise((res) => cv.toBlob(res, "image/jpeg", 0.92));
      if (!blob) throw new Error("Render failed");
      const file = new File([blob], `text-card-${Date.now()}.jpg`, { type: "image/jpeg" });
      // Upload via existing media pipeline
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const body = {
        caption: text.slice(0, 240),
        media_path: up.path,
        media_type: "image",
        privacy: "public",
        music_id: music?.id || null,
      };
      await api.post("/posts", body);
      toast.success("Text card posted!");
      nav("/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Post failed");
    } finally { setPosting(false); }
  };

  const downloadCard = () => {
    const cv = canvasRef.current;
    const link = document.createElement("a");
    link.download = `giffwurldaunce-card-${Date.now()}.jpg`;
    link.href = cv.toDataURL("image/jpeg", 0.92);
    link.click();
  };

  const exportVideo = async ({ shouldPost }) => {
    if (!text.trim()) { toast.error("Add some text first"); return; }
    setExporting({ phase: "starting", percent: 0 });
    try {
      // Fast path when posting: upload raw WebM (Mux re-encodes server-side, way faster).
      // Save-to-device path: transcode to MP4 for iOS compatibility.
      const blob = await recordVideo({
        canvas: canvasRef.current,
        duration,
        drawFrame,
        audioUrl: music?.audio_url || null,
        fps: 30,
        format: shouldPost ? "webm" : "mp4",
        onProgress: (p) => setExporting(p),
      });
      const ext = shouldPost ? "webm" : "mp4";
      const mime = shouldPost ? "video/webm" : "video/mp4";
      const file = new File([blob], `studio-${Date.now()}.${ext}`, { type: mime });

      if (!shouldPost) {
        const url = URL.createObjectURL(file);
        const link = document.createElement("a");
        link.download = file.name;
        link.href = url;
        link.click();
        setTimeout(() => URL.revokeObjectURL(url), 5000);
        toast.success("MP4 saved!");
      } else {
        setExporting({ phase: "uploading", percent: 0 });
        const uploaded = await uploadMedia(file, { onProgress: (pct) => setExporting({ phase: "uploading", percent: pct }) });
        const body = {
          caption: text.slice(0, 240),
          media_path: uploaded.path,
          media_type: uploaded.media_type,
          privacy: "public",
          music_id: music?.id || null,
        };
        if (uploaded.mux) {
          body.mux_upload_id = uploaded.mux.upload_id;
          body.mux_passthrough = uploaded.mux.passthrough;
          body.mux_status = uploaded.mux.status;
        }
        await api.post("/posts", body);
        toast.success("Posted! Video will be ready in a few seconds.");
        nav("/");
        return;
      }
    } catch (e) {
      toast.error(e?.message || "Video export failed");
    } finally {
      setExporting(null);
      // Re-draw static preview after export to leave canvas clean
      drawFrame(canvasRef.current, 1);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-ink pb-32" data-testid="creator-studio">
      <div className="px-5 pt-6 flex items-center gap-3">
        <button onClick={() => nav(-1)} className="w-10 h-10 rounded-full crystal-glass flex items-center justify-center text-cream" data-testid="studio-back">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-bold">Creator Studio</p>
          <h1 className="text-cream text-3xl font-black tracking-tighter">Text to video</h1>
        </div>
      </div>

      {/* AI tools strip */}
      <div className="px-5 mt-5 flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="studio-ai-tools">
        {[
          { to: "/ai-voice", icon: Waveform, label: "AI Voice", sub: "ElevenLabs TTS", testid: "studio-tool-ai-voice" },
          { to: "/ai-voiceover", icon: Microphone, label: "AI Voiceover", sub: "Narrate a video", testid: "studio-tool-ai-voiceover" },
          { to: "/ai-video", icon: FilmStrip, label: "AI Text-to-Video", sub: "Script → video", testid: "studio-tool-ai-video" },
          { to: "/ai-song", icon: MusicNotes, label: "AI Song Studio", sub: "Make an original song", testid: "studio-tool-ai-song" },
          { to: "/gifts/create", icon: Gift, label: "Create a Gift", sub: "Design & sell", testid: "studio-tool-gift-creator" },
          { to: "/gifts", icon: Storefront, label: "Gift Shop", sub: "Community gifts", testid: "studio-tool-gift-shop" },
        ].map(({ to, icon: I, label, sub, testid }) => (
          <Link key={to} to={to} data-testid={testid} className="flex-shrink-0 w-36 rounded-2xl bg-white/5 border border-white/10 p-3 hover:border-papaya/50 transition-colors">
            <I size={18} className="text-papaya" weight="duotone" />
            <p className="text-cream font-black text-sm mt-1.5">{label}</p>
            <p className="text-cream/50 text-[11px]">{sub}</p>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-5 mt-6">
        {/* Preview */}
        <section>
          <div className="rounded-3xl overflow-hidden border border-white/10 bg-black aspect-[3/4] relative">
            <canvas ref={canvasRef} data-testid="studio-canvas" className="w-full h-full block" />
            {exporting && (
              <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm flex flex-col items-center justify-center" data-testid="studio-exporting">
                <div className="w-16 h-16 rounded-full border-2 border-papaya border-t-transparent animate-spin mb-4" />
                <p className="text-papaya text-[10px] tracking-[0.3em] uppercase font-black">
                  {exporting.phase === "recording" && "Recording video"}
                  {exporting.phase === "encoding" && "Encoding MP4"}
                  {exporting.phase === "uploading" && "Uploading"}
                  {exporting.phase === "starting" && "Preparing"}
                  {exporting.phase === "done" && "Done"}
                </p>
                <p className="text-cream font-black text-2xl mt-1">{exporting.percent || 0}%</p>
                <div className="w-44 h-1 bg-white/10 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-papaya transition-all" style={{ width: `${exporting.percent || 0}%` }} />
                </div>
                {exporting.phase === "encoding" && (
                  <p className="text-cream/40 text-[10px] mt-3 max-w-[260px] text-center leading-relaxed">
                    Transcoding WebM → MP4 with ffmpeg.wasm. This step is slow the first time (downloads ~30MB core).
                  </p>
                )}
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            <button onClick={downloadCard} data-testid="studio-download" className="px-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-cream text-xs font-black tracking-[0.2em] uppercase flex items-center justify-center gap-2 hover:bg-white/10">
              <Download size={14} /> Save image
            </button>
            <button onClick={postCard} data-testid="studio-post" disabled={posting || exporting} className="px-4 py-2.5 rounded-full bg-white/5 border border-white/15 text-cream text-xs font-black tracking-[0.2em] uppercase flex items-center justify-center gap-2 hover:bg-white/10 disabled:opacity-50">
              <UploadSimple size={14} /> {posting ? "Posting…" : "Post image"}
            </button>
          </div>
          <div className="mt-3">
            <label className="text-cream/50 text-[10px] tracking-[0.25em] uppercase font-bold mb-2 flex items-center gap-1">
              <Clock size={12} weight="bold" /> Video duration · {duration}s
            </label>
            <input
              type="range" min={3} max={15} step={1}
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10))}
              data-testid="studio-duration"
              className="w-full"
              disabled={!!exporting}
            />
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <button onClick={() => exportVideo({ shouldPost: false })} data-testid="studio-export-mp4" disabled={!!exporting} className="px-4 py-2.5 rounded-full bg-white/5 border border-papaya/40 text-papaya text-xs font-black tracking-[0.2em] uppercase flex items-center justify-center gap-2 hover:bg-papaya/10 disabled:opacity-50">
              <FilmStrip size={14} weight="fill" /> Export MP4
            </button>
            <button onClick={() => exportVideo({ shouldPost: true })} data-testid="studio-post-video" disabled={!!exporting} className="px-4 py-2.5 rounded-full bg-papaya text-ink text-xs font-black tracking-[0.2em] uppercase flex items-center justify-center gap-2 hover:bg-papaya-hover papaya-glow disabled:opacity-50">
              <UploadSimple size={14} /> Post video
            </button>
          </div>
        </section>

        {/* Controls */}
        <section className="space-y-5">
          {/* Text */}
          <div>
            <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 flex items-center gap-2">
              <TextAa size={14} weight="bold" /> Text
            </label>
            <textarea
              data-testid="studio-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-2xl p-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-papaya text-sm resize-none"
              placeholder="Type your message…"
            />
            <button onClick={aiCaption} disabled={aiLoading} data-testid="studio-ai" className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-papaya/10 text-papaya text-[10px] tracking-[0.2em] uppercase font-black disabled:opacity-50">
              <MagicWand size={12} weight="fill" /> {aiLoading ? "Thinking…" : "AI caption"}
            </button>
          </div>

          {/* Color scheme */}
          <div>
            <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 flex items-center gap-2">
              <Palette size={14} weight="bold" /> Color scheme
            </label>
            <div className="grid grid-cols-4 gap-2" data-testid="studio-schemes">
              {SCHEMES.map((s) => (
                <button
                  key={s.id}
                  data-testid={`studio-scheme-${s.id}`}
                  onClick={() => setScheme(s)}
                  className={`aspect-square rounded-2xl border-2 transition-all ${scheme.id === s.id ? "border-papaya scale-95" : "border-transparent hover:border-white/20"}`}
                  style={{ background: s.bg }}
                  title={s.label}
                />
              ))}
            </div>
          </div>

          {/* Font + size */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Font</label>
              <select data-testid="studio-font" value={fontIdx} onChange={(e) => setFontIdx(parseInt(e.target.value, 10))} className="w-full bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-cream text-sm focus:outline-none focus:border-papaya">
                {FONTS.map((f, i) => <option key={f.id} value={i}>{f.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Size</label>
              <input data-testid="studio-fontsize" type="range" min="32" max="140" step="2" value={fontSize} onChange={(e) => setFontSize(parseInt(e.target.value, 10))} className="w-full" />
            </div>
          </div>

          {/* Voice-over (TTS) */}
          <div>
            <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 flex items-center gap-2">
              <SpeakerHigh size={14} weight="bold" /> Voice-over
            </label>
            <div className="grid grid-cols-2 gap-2">
              <select data-testid="studio-voice" value={voiceURI} onChange={(e) => setVoiceURI(e.target.value)} className="bg-white/5 border border-white/10 rounded-2xl px-3 py-2 text-cream text-xs focus:outline-none focus:border-papaya">
                {voices.length === 0 && <option>System default</option>}
                {voices.map((v) => <option key={v.id} value={v.id}>{v.name} · {v.lang}</option>)}
              </select>
              <div className="flex items-center gap-1.5">
                <label className="text-cream/50 text-[10px] tracking-[0.2em] uppercase">Rate</label>
                <input data-testid="studio-rate" type="range" min="0.5" max="2" step="0.1" value={rate} onChange={(e) => setRate(parseFloat(e.target.value))} className="flex-1" />
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              {speaking ? (
                <button onClick={stopSpeak} data-testid="studio-stop" className="flex-1 px-4 py-2 rounded-full bg-red-500/20 text-red-200 text-xs font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5">
                  <Stop weight="fill" size={12} /> Stop
                </button>
              ) : (
                <button onClick={previewSpeak} data-testid="studio-speak" className="flex-1 px-4 py-2 rounded-full bg-jam-green/20 text-jam-green text-xs font-black tracking-[0.2em] uppercase flex items-center justify-center gap-1.5">
                  <SpeakerHigh weight="fill" size={12} /> Preview voice-over
                </button>
              )}
            </div>
          </div>

          {/* Sound effects */}
          <div>
            <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 block">Sound effects</label>
            <div className="grid grid-cols-4 gap-2" data-testid="studio-sfx-grid">
              {sfx.map((s) => (
                <button
                  key={s.id}
                  data-testid={`studio-sfx-${s.id}`}
                  onClick={() => playSfx(s.id)}
                  className="aspect-square rounded-2xl bg-white/5 border border-white/10 hover:border-papaya text-cream transition-colors flex flex-col items-center justify-center"
                >
                  <span className="text-2xl">{s.emoji}</span>
                  <span className="text-[10px] mt-1 tracking-[0.1em] uppercase">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Music */}
          <div>
            <label className="text-cream/70 text-xs tracking-[0.2em] uppercase font-bold mb-2 flex items-center gap-2">
              <MusicNotes size={14} weight="bold" /> Background music
            </label>
            <button data-testid="studio-music-open" onClick={() => setMusicOpen(true)} className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl border transition-colors ${music ? "bg-papaya/10 border-papaya/40" : "bg-white/5 border-white/10 hover:border-white/20"}`}>
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-papaya/30 via-jam-green/20 to-jam-gold/20 flex items-center justify-center text-lg">
                {music ? music.cover_emoji : <MusicNotes size={16} className="text-papaya" weight="fill" />}
              </div>
              <div className="flex-1 min-w-0 text-left">
                {music ? (
                  <>
                    <p className="text-cream font-bold text-sm truncate">{music.title}</p>
                    <p className="text-cream/50 text-[11px] truncate">{music.artist} · {music.mood}</p>
                  </>
                ) : (
                  <p className="text-cream/70 text-sm">Pick a sound</p>
                )}
              </div>
            </button>
          </div>
        </section>
      </div>

      {musicOpen && (
        <MusicPicker
          selectedId={music?.id}
          onClose={() => setMusicOpen(false)}
          onSelect={(m) => { setMusic(m); setMusicOpen(false); }}
        />
      )}
    </div>
  );
}
